/**
 * Auth store – Zustand with persistence.
 * Manages JWT tokens, user state, login/logout.
 */
import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { authApi } from "@services/api/auth";
import { signOut, isSignOutInProgress } from "@utils/signOut";

export { isSignOutInProgress };

export interface AuthUser {
  id: string;
  username: string;
  email: string;
  displayName: string;
  plan: "free" | "plus" | "pro" | "corporate";
  emailVerified: boolean;
  avatar?: string;
}

interface AuthState {
  user: AuthUser | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  login: (email: string, password: string, totpCode?: string) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => Promise<void>;
  updateUser: (updates: Partial<AuthUser>) => void;
  setTokens: (access: string, refresh: string) => void;
}

interface RegisterData {
  email: string;
  nickname?: string;
  nicknameRarity?: string;
  nicknameScore?: number;
  displayName?: string;
  password: string;
  confirmPassword: string;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,

      login: async (email, password, totpCode) => {
        set({ isLoading: true });
        try {
          const { data } = await authApi.login({ email, password, totp_code: totpCode });
          const { access, refresh, user_id, username, plan, email_verified } = data.data;
          set({
            accessToken: access,
            refreshToken: refresh,
            isAuthenticated: true,
            user: {
              id: user_id,
              username,
              email,
              displayName: username,
              plan,
              emailVerified: email_verified,
            },
          });
        } finally {
          set({ isLoading: false });
        }
      },

      register: async (data) => {
        set({ isLoading: true });
        try {
          const res = await authApi.register(data);
          const { access, refresh, user_id, username, plan, email_verified } = res.data.data;
          set({
            accessToken: access,
            refreshToken: refresh,
            isAuthenticated: true,
            user: {
              id: user_id,
              username,
              email: data.email,
              displayName: data.displayName || username,
              plan,
              emailVerified: email_verified,
            },
          });
        } finally {
          set({ isLoading: false });
        }
      },

      logout: async () => {
        await signOut();
      },

      updateUser: (updates) => {
        set((state) => ({
          user: state.user ? { ...state.user, ...updates } : null,
        }));
      },

      setTokens: (access, refresh) => {
        set({ accessToken: access, refreshToken: refresh, isAuthenticated: true });
      },
    }),
    {
      name: "lifegram-auth",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
